import { PortableText, PortableTextComponents } from '@portabletext/react'

import cn from 'clsx'
import { File } from 'sanity'

import ImageBox from '../ImageBox/ImageBox'

import { type Copy as CopyType } from '../../../../apps/benmax/src/types/sanity.types'

import s from './Copy.module.sass'

interface ICopy {
    copy?: CopyType
    size?: string
    className?: string
    project?: boolean
}

export default function Copy({ copy, size, className = '', project = false }: ICopy) {
    if (!copy) return null

    const customPortableComponents: PortableTextComponents = {
        types: {
            imageGallery: ({
                value: { images },
            }: {
                value: {
                    images: any[]
                }
            }) => {
                if (!images || images.length === 0) return null

                return (
                    <div className={s.ImageGallery}>
                        {images?.map((_image, index) => {
                            const { alt, image, caption } = _image

                            return (
                                <figure className={s.Image} key={index}>
                                    {image && <ImageBox image={image} alt={alt} />}
                                    <figcaption>
                                        {caption && (
                                            <span
                                                className="copy small"
                                                dangerouslySetInnerHTML={{
                                                    __html: caption,
                                                }}
                                            />
                                        )}
                                    </figcaption>
                                </figure>
                            )
                        })}
                    </div>
                )
            },
            portableImage: ({
                value: { alt, image, caption },
            }: {
                value: {
                    alt: string
                    image: File
                    caption: string
                }
            }) => {
                return (
                    <figure className={s.Image}>
                        {image && <ImageBox image={image} alt={alt} />}
                        <figcaption>
                            {caption && (
                                <span
                                    className="copy small"
                                    dangerouslySetInnerHTML={{
                                        __html: caption,
                                    }}
                                />
                            )}
                        </figcaption>
                    </figure>
                )
            },
            horizontalRule: () => <hr />,
            blockquote: ({
                value: { quote, title, position, company },
            }: {
                value: {
                    quote: string
                    title: string
                    position: string
                    company: string
                }
            }) => {
                const hasCaption = title || position || company

                return (
                    <figure className={cn(s.Blockquote, {
                        [s.IsProject as string]: project,
                        [s.NoCaption as string]: !hasCaption,
                    })}>
                        {quote && (
                            <blockquote>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: quote,
                                    }}
                                />
                            </blockquote>
                        )}
                        {hasCaption ? (
                            <figcaption>
                                <div>
                                    {title && (
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: title,
                                            }}
                                        />
                                    )}
                                    {position && (
                                        <span
                                            className="smallCaps"
                                            dangerouslySetInnerHTML={{
                                                __html: position,
                                            }}
                                        />
                                    )}
                                    {company && (
                                        <span
                                            className="smallCaps"
                                            dangerouslySetInnerHTML={{
                                                __html: company,
                                            }}
                                        />
                                    )}
                                </div>
                            </figcaption>
                        ) : null}
                    </figure>
                )
            },
        },
        marks: {
            link: ({ children, value }) => {
                return (
                    <a href={value.href} target="_blank">
                        {children}
                    </a>
                )
            },
        },
    }

    return (
        <div className={cn(s.Copy, className, 'copy', size && size)}>
            <PortableText value={copy} components={customPortableComponents} />
        </div>
    )
}
